import React, { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Settings,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  Crown,
  Sparkles,
  Briefcase,
  Compass,
  Target,
  Inbox,
  AlertTriangle,
  FileText,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useBdAssistantEligibility } from "../hooks/useBdAssistantEligibility";
import api from "../api";

type NavItem = { to: string; icon: React.ElementType; label: string };

type NavSection = {
  title: string;
  items: NavItem[];
};

const Navigation: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [inboxPopupOpen, setInboxPopupOpen] = useState(false);
  const inboxRef = useRef<HTMLLIElement>(null);

  // Attention items for SUPER_ADMIN hover popup on Alert Inbox
  const [attentionItems, setAttentionItems] = useState<{ label: string; count: number; tab: string; icon: React.ElementType; color: string }[]>([]);

  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const isAdmin = ['ADMIN', 'MASTER', 'SUPER_ADMIN'].includes(user?.role || '');
  // BD-04 — hides the nav entry for ineligible users as a courtesy; every
  // Business Development API re-checks the same flag server-side via
  // requireBdAssistantEligible, so this is not the security control.
  const { allowed: bdEligible } = useBdAssistantEligibility();

  // Attention breakdown items for popup
  useEffect(() => {
    if (!isSuperAdmin) return;
    const fetchAttention = async () => {
      const items: typeof attentionItems = [];
      try {
        const res = await api.get('/api/admin/global-alerts/summary');
        const total = res.data?.summary?.total ?? 0;
        if (total > 0) items.push({ label: 'Unresolved Alerts', count: total, tab: 'alert-inbox', icon: AlertTriangle, color: 'text-red-400' });
      } catch { /* silent */ }
      setAttentionItems(items);
    };
    fetchAttention();
    const interval = setInterval(fetchAttention, 60_000);
    return () => clearInterval(interval);
  }, [isSuperAdmin]);

  const navSections: NavSection[] = [
    {
      title: "Analytical Tools",
      items: [
        { to: "/app/smart-start", icon: Compass, label: "Smart Router" },
        { to: "/app/intelligence", icon: Sparkles, label: "Jarvis Intelligence" },
        { to: "/app/ai-council", icon: Crown, label: "AI Council" },
        { to: "/app/sop-builder", icon: FileText, label: "Process Documentation" },
      ],
    },
    {
      title: "Project Management",
      items: [
        { to: "/app/project-management", icon: Briefcase, label: "Project Management" },
      ],
    },
    ...(bdEligible ? [{
      title: "Business Development",
      items: [
        { to: "/app/business-development", icon: Target, label: "Business Development" },
      ],
    }] : []),
  ];

  const bottomItems: NavItem[] = [
    ...(isAdmin ? [{ to: "/app/admin", icon: Settings, label: user?.role === "SUPER_ADMIN" ? "Admin" : "Settings" }] : []),
    ...(user?.role === "SUPER_ADMIN" ? [{ to: "/app/executive-dashboard", icon: TrendingUp, label: "Executive Dashboard" }] : []),
    ...(isSuperAdmin ? [{ to: "/app/alert-inbox", icon: Inbox, label: "Alert Inbox" }] : []),
  ];

  const renderNavItem = (item: NavItem) => (
    <li key={item.to}>
      <NavLink
        to={item.to}
        title={isCollapsed ? item.label : ""}
        className={({ isActive }) =>
          `flex items-center ${
            isCollapsed ? "justify-center w-12 h-12 mx-auto" : "space-x-3 pl-4 pr-3 py-3"
          } rounded-xl transition-colors text-[15px] ${
            isActive
              ? "bg-electric-blue text-super-dark-purple font-semibold"
              : "text-white/60 hover:bg-white/10 hover:text-white"
          }`
        }
      >
        <span className="relative flex-shrink-0">
          <item.icon className="w-5 h-5" />
        </span>
        {!isCollapsed && (
          <span className="font-medium">{item.label}</span>
        )}
      </NavLink>
    </li>
  );

  const totalAttention = attentionItems.reduce((s, a) => s + a.count, 0);

  return (
    <nav
      className={`${
        isCollapsed ? "w-20" : "w-64"
      } bg-super-dark-purple text-white h-[calc(100vh-88px)] transition-all duration-300 relative flex flex-col`}
      data-tour="navigation"
    >
      {/* Toggle Button */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute -right-3 top-6 bg-electric-blue text-super-dark-purple rounded-full p-1 hover:bg-opacity-90 transition-colors z-10 shadow-lg"
        title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
      >
        {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
      </button>

      <div className="flex-1 overflow-y-auto bg-super-dark-purple">
        <div className="py-5 px-3 flex flex-col gap-7">
          {navSections.map((section) => (
            <div key={section.title}>
              {!isCollapsed ? (
                <>
                  <p className="text-base font-bold text-white/90 px-2 mb-3 tracking-wide">
                    {section.title}
                  </p>
                  <ul className="space-y-1">
                    {section.items.map(renderNavItem)}
                  </ul>
                </>
              ) : (
                <ul className="flex flex-col items-center gap-1">
                  {section.items.map(renderNavItem)}
                </ul>
              )}
            </div>
          ))}

          {/* Bottom items — General */}
          <div>
            {!isCollapsed && (
              <p className="text-base font-bold text-white/90 px-2 mb-3 tracking-wide">
                General
              </p>
            )}
            <ul className={isCollapsed ? "flex flex-col items-center gap-1" : "space-y-1"}>
              {bottomItems.map((item) => {
                const isInboxItem = item.to === '/app/alert-inbox';

                if (isInboxItem && isSuperAdmin) {
                  return (
                    <li
                      key={item.to}
                      className="relative"
                      ref={inboxRef}
                    >
                      {/* Hover popup — breakdown of items needing attention */}
                      {inboxPopupOpen && attentionItems.length > 0 && (
                        <div
                          className={`absolute ${isCollapsed ? 'left-14' : 'left-0'} bottom-full mb-2 w-56 bg-[#1e1b3a] border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden`}
                          onMouseEnter={() => setInboxPopupOpen(true)}
                          onMouseLeave={() => setInboxPopupOpen(false)}
                        >
                          <p className="text-[11px] font-bold text-white/50 uppercase tracking-widest px-3 pt-3 pb-1">Needs Attention</p>
                          {attentionItems.map((a) => (
                            <button
                              key={a.tab}
                              onClick={() => {
                                setInboxPopupOpen(false);
                                if (a.tab === 'alert-inbox') {
                                  navigate('/app/alert-inbox');
                                } else {
                                  navigate(`/app/admin?tab=${a.tab}`);
                                }
                              }}
                              className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-white/10 transition-colors text-left"
                            >
                              <a.icon className={`w-4 h-4 flex-shrink-0 ${a.color}`} />
                              <span className="flex-1 text-sm text-white/80">{a.label}</span>
                              <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full bg-white/10 ${a.color}`}>{a.count}</span>
                            </button>
                          ))}
                          <div className="border-t border-white/10 mt-1">
                            <button
                              onClick={() => { setInboxPopupOpen(false); navigate('/app/alert-inbox'); }}
                              className="w-full text-center text-xs text-white/50 hover:text-white py-2 transition-colors"
                            >
                              Open Alert Inbox →
                            </button>
                          </div>
                        </div>
                      )}
                      <NavLink
                        to={item.to}
                        title={isCollapsed ? item.label : ""}
                        className={({ isActive }) =>
                          `flex items-center ${isCollapsed ? "justify-center w-12 h-12 mx-auto" : "space-x-3 pl-4 pr-3 py-3"} rounded-xl transition-colors text-[15px] ${
                            isActive ? "bg-electric-blue text-super-dark-purple font-semibold" : "text-white/60 hover:bg-white/10 hover:text-white"
                          }`
                        }
                      >
                        <span
                          className="relative flex-shrink-0"
                          onMouseEnter={() => setInboxPopupOpen(true)}
                          onMouseLeave={() => setInboxPopupOpen(false)}
                        >
                          <item.icon className="w-5 h-5" />
                          {totalAttention > 0 && (
                            <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
                              {totalAttention > 99 ? '99+' : totalAttention}
                            </span>
                          )}
                        </span>
                        {!isCollapsed && (
                          <span className="font-medium flex items-center gap-2 flex-1">
                            {item.label}
                            {totalAttention > 0 && (
                              <span className="ml-auto min-w-[20px] h-5 px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                                {totalAttention > 99 ? '99+' : totalAttention}
                              </span>
                            )}
                          </span>
                        )}
                      </NavLink>
                    </li>
                  );
                }

                return renderNavItem(item);
              })}
            </ul>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
